import { useMemo } from 'react';
import type { WebFunnelStepMetric, WebFunnelTransition } from '@/hooks/useWebFunnelReport';

const NODE_W = 118;
const NODE_H = 44;
const GAP_X = 72;
const PAD = 16;

interface FunnelSankeyProps {
    steps: WebFunnelStepMetric[];
    transitions: WebFunnelTransition[];
    maxLinks?: number;
}

/** Sankey ligero (SVG) para transiciones explícitas funnel_transition. */
export function FunnelSankey({ steps, transitions, maxLinks = 24 }: FunnelSankeyProps) {
    const layout = useMemo(() => buildLayout(steps, transitions, maxLinks), [steps, transitions, maxLinks]);

    if (!layout.links.length && steps.length < 2) {
        return (
            <p className="text-sm text-muted-foreground">
                Sin transiciones registradas. El embudo lineal de arriba sigue siendo válido.
            </p>
        );
    }

    return (
        <svg
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            className="w-full max-h-[420px] text-foreground"
            role="img"
            aria-label="Diagrama de flujo entre pasos del embudo"
        >
            {layout.links.map((link) => (
                <path
                    key={link.id}
                    d={link.path}
                    fill="none"
                    stroke="hsl(var(--primary))"
                    strokeOpacity={0.25 + link.weight * 0.55}
                    strokeWidth={2 + link.weight * 10}
                />
            ))}
            {layout.nodes.map((node) => (
                <g key={node.id} transform={`translate(${node.x}, ${node.y})`}>
                    <rect
                        width={NODE_W}
                        height={NODE_H}
                        rx={8}
                        className="fill-card stroke-border"
                        strokeWidth={1}
                    />
                    <text x={8} y={16} className="fill-muted-foreground text-[9px]">
                        {node.id.length > 14 ? `${node.id.slice(0, 12)}…` : node.id}
                    </text>
                    <text x={8} y={32} className="fill-foreground text-[11px] font-semibold">
                        {node.users.toLocaleString('es-ES')}
                    </text>
                </g>
            ))}
        </svg>
    );
}

function buildLayout(
    steps: WebFunnelStepMetric[],
    transitions: WebFunnelTransition[],
    maxLinks: number,
) {
    const stepIndex = new Map<string, number>();
    steps.forEach((step, index) => stepIndex.set(step.id, index));

    const nodeIds = new Set<string>();
    transitions.slice(0, maxLinks).forEach((t) => {
        nodeIds.add(t.from_step);
        nodeIds.add(t.to_step);
    });

    const ordered = [...nodeIds].sort((a, b) => {
        const ia = stepIndex.has(a) ? stepIndex.get(a)! : 999;
        const ib = stepIndex.has(b) ? stepIndex.get(b)! : 999;
        return ia - ib || a.localeCompare(b);
    });

    const colOf = new Map<string, number>();
    ordered.forEach((id, i) => colOf.set(id, Math.min(i, 6)));

    const maxUsers = Math.max(
        1,
        ...transitions.map((t) => t.active_users),
        ...steps.map((s) => s.active_users),
    );

    const nodes = ordered.map((id) => {
        const col = colOf.get(id) ?? 0;
        const row = ordered.filter((other) => (colOf.get(other) ?? 0) === col).indexOf(id);
        const users =
            transitions.find((t) => t.from_step === id)?.active_users
            ?? steps.find((s) => s.id === id)?.active_users
            ?? 0;
        return {
            id,
            x: PAD + col * (NODE_W + GAP_X),
            y: PAD + row * (NODE_H + 20),
            users,
        };
    });

    const nodePos = new Map(nodes.map((n) => [n.id, n]));

    const links = transitions.slice(0, maxLinks).map((t, index) => {
        const from = nodePos.get(t.from_step);
        const to = nodePos.get(t.to_step);
        if (!from || !to) {
            return { id: `skip-${index}`, path: '', weight: 0 };
        }
        const x1 = from.x + NODE_W;
        const y1 = from.y + NODE_H / 2;
        const x2 = to.x;
        const y2 = to.y + NODE_H / 2;
        const cx = (x1 + x2) / 2;
        const weight = t.active_users / maxUsers;
        return {
            id: `${t.from_step}->${t.to_step}-${index}`,
            path: `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`,
            weight,
        };
    }).filter((l) => l.path);

    const width = PAD * 2 + (Math.max(...nodes.map((n) => n.x), 0) + NODE_W);
    const height = PAD * 2 + (Math.max(...nodes.map((n) => n.y), 0) + NODE_H);

    return { nodes, links, width: Math.max(width, 320), height: Math.max(height, 160) };
}
